import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Badge } from '../ui/badge'
import { Progress } from '../ui/progress'
import { Combobox } from '../ui/combobox'
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from '../ui/select'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../ui/table'
import {
    AlertTriangle,
    CheckCircle2,
    Download,
    FileSpreadsheet,
    ImageIcon,
    Loader2,
    RotateCcw,
    Upload,
    Users,
    X,
    XCircle,
} from 'lucide-react'
import { toast } from 'sonner'
import { extractErrorMessage } from '@/common/extractErrorMessage'
import { CsvTable, parseCsv, toCsv } from '@/lib/csv'
import {
    ColumnMapping,
    IGNORE_COLUMN,
    IMPORT_FIELDS,
    ImportFieldKey,
    ImportRow,
    autoMapColumns,
    buildImportRows,
    buildTemplateRows,
    patchFieldsFor,
    summarizeRows,
} from '@/lib/identityImport'
import { RemoteImage, fetchRemoteImage } from '@/lib/remoteImage'
import { isEventActive } from '@/lib/eventDates'
import { useApplicationStore } from '@/stores/useApplicationStore'
import { useSessionStore } from '@/stores/useSessionStore'
import IdentityEndpoints from '@/endpoints/IdentityEndpoints'
import EventEndpoints from '@/endpoints/EventEndpoints'
import { SimpleEventWithProvisionings } from '@/model/Event'

/** Simultaneous identity creations. Kept low so a big file can't swamp the API. */
const IMPORT_CONCURRENCY = 3
/** Above this many existing identities we skip the client-side duplicate pre-check. */
const DUPLICATE_SCAN_LIMIT = 5000
const NO_EVENT = '__none__'
const NO_ACCREDITATION = '__default__'
const PREVIEW_ROWS = 10

type RowStatus = 'created' | 'partial' | 'failed'

interface RowResult {
    rowNumber: number
    name: string
    status: RowStatus
    identityId?: number
    messages: string[]
}

const FIELD_GROUP_LABELS: Record<string, string> = {
    name: 'Name',
    contact: 'Contact',
    work: 'Work',
    address: 'Address',
    other: 'Other',
}

const GROUP_ORDER = ['name', 'contact', 'work', 'address', 'other']

/**
 * Starter CSV carrying a column for every importable field. Blank cells are
 * never sent, so unused columns can be emptied or deleted.
 */
const templateCsv = (includeExampleRow: boolean) =>
    toCsv(buildTemplateRows({ includeExampleRow }))

const downloadCsv = (contents: string, filename: string) => {
    const url = URL.createObjectURL(new Blob([contents], { type: 'text/csv;charset=utf-8;' }))
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
    URL.revokeObjectURL(url)
}

export const IdentityImportPage: React.FC = () => {
    const { hasAdmin } = useSessionStore()
    const isAdmin = hasAdmin()

    const { clients, refreshClients } = useApplicationStore()

    // Destination
    const [clientId, setClientId] = useState('')
    const [defaultBadgeRole, setDefaultBadgeRole] = useState('')
    const [clientEvents, setClientEvents] = useState<SimpleEventWithProvisionings[]>([])
    const [eventsLoading, setEventsLoading] = useState(false)
    const [eventId, setEventId] = useState(NO_EVENT)
    const [roleId, setRoleId] = useState('')
    const [accreditationId, setAccreditationId] = useState(NO_ACCREDITATION)
    const [secondaryRoleName, setSecondaryRoleName] = useState('')

    // Duplicate pre-check
    const [existingEmails, setExistingEmails] = useState<Set<string> | null>(null)
    const [scanningExisting, setScanningExisting] = useState(false)

    // File + mapping
    const [fileName, setFileName] = useState('')
    const [table, setTable] = useState<CsvTable | null>(null)
    const [mapping, setMapping] = useState<ColumnMapping>({})
    const [dragging, setDragging] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    // Import run
    const [importing, setImporting] = useState(false)
    const [completed, setCompleted] = useState(0)
    const [results, setResults] = useState<RowResult[]>([])
    const [finished, setFinished] = useState(false)
    const cancelled = useRef(false)

    useEffect(() => {
        if (!isAdmin) return
        if (!clients || clients.length === 0) refreshClients()
    }, [isAdmin])

    const selectedClient = clients?.find((c) => String(c.id) === clientId)

    // Load the client's events (with their roles) so staff can be provisioned on the way in.
    useEffect(() => {
        setClientEvents([])
        setEventId(NO_EVENT)
        setRoleId('')
        setAccreditationId(NO_ACCREDITATION)
        if (!clientId) return

        let cancelledLoad = false
        setEventsLoading(true)
        EventEndpoints.getEventListForProvisioning(Number(clientId))
            .then((list) => {
                if (cancelledLoad) return
                const sorted = [...(list || [])].sort(
                    (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
                )
                setClientEvents(sorted)
            })
            .catch((error) => {
                if (!cancelledLoad) {
                    toast.error(extractErrorMessage(error, "Failed to load the client's events"))
                }
            })
            .finally(() => {
                if (!cancelledLoad) setEventsLoading(false)
            })

        return () => {
            cancelledLoad = true
        }
    }, [clientId])

    // Pre-load existing emails for the client so duplicates surface before we start writing.
    useEffect(() => {
        setExistingEmails(null)
        if (!clientId) return

        let cancelledScan = false
        setScanningExisting(true)
        IdentityEndpoints.getIdentityCount(Number(clientId), undefined, true, true)
            .then(({ count }) => {
                if (cancelledScan) return null
                if (count > DUPLICATE_SCAN_LIMIT) return null
                return IdentityEndpoints.getIdentityList(
                    Number(clientId),
                    undefined,
                    undefined,
                    DUPLICATE_SCAN_LIMIT,
                    0,
                    undefined,
                    true,
                    true
                )
            })
            .then((identities) => {
                if (cancelledScan || !identities) return
                setExistingEmails(
                    new Set(
                        identities
                            .map((i) => (i.email || '').trim().toLowerCase())
                            .filter(Boolean)
                    )
                )
            })
            .catch(() => {
                // Non-fatal: the server still rejects duplicate emails on create.
            })
            .finally(() => {
                if (!cancelledScan) setScanningExisting(false)
            })

        return () => {
            cancelledScan = true
        }
    }, [clientId])

    // Provisioning a finished event is never what the user wants, so past events
    // are kept out of the dropdown entirely.
    const provisionableEvents = useMemo(
        () => clientEvents.filter((e) => isEventActive(e)),
        [clientEvents]
    )
    const pastEventCount = clientEvents.length - provisionableEvents.length
    const selectedEvent = provisionableEvents.find((e) => String(e.id) === eventId)

    const rows: ImportRow[] = useMemo(() => {
        if (!table) return []
        return buildImportRows(table.rows, mapping, {
            defaultBadgeRoleName: defaultBadgeRole,
            existingEmails: existingEmails || undefined,
        })
    }, [table, mapping, defaultBadgeRole, existingEmails])

    const summary = useMemo(() => summarizeRows(rows), [rows])

    /** Rows that will trigger a photo download, so the review step can warn. */
    const photoCount = useMemo(
        () => rows.filter((r) => r.errors.length === 0 && r.values.pictureUrl).length,
        [rows]
    )

    /** Fields currently claimed by a column, so the dropdowns can grey out duplicates. */
    const mappedFields = useMemo(() => {
        const claimed = new Set<string>()
        Object.keys(mapping).forEach((k) => {
            const value = mapping[Number(k)]
            if (value && value !== IGNORE_COLUMN) claimed.add(value)
        })
        return claimed
    }, [mapping])

    const resetRun = () => {
        setResults([])
        setCompleted(0)
        setFinished(false)
    }

    const loadFile = useCallback(async (file: File) => {
        if (!file) return
        try {
            const text = await file.text()
            const parsed = parseCsv(text)
            if (parsed.headers.length === 0) {
                toast.error('That file has no readable header row.')
                return
            }
            if (parsed.rows.length === 0) {
                toast.error('That file has a header but no data rows.')
                return
            }
            setFileName(file.name)
            setTable(parsed)
            setMapping(autoMapColumns(parsed.headers))
            resetRun()
            toast.success(
                `Read ${parsed.rows.length} row${parsed.rows.length === 1 ? '' : 's'} from ${file.name}`
            )
        } catch (error) {
            toast.error(extractErrorMessage(error, 'Failed to read that file'))
        }
    }, [])

    const clearFile = () => {
        setFileName('')
        setTable(null)
        setMapping({})
        resetRun()
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    const importRow = async (row: ImportRow): Promise<RowResult> => {
        const name = `${row.values.firstName || ''} ${row.values.lastName || ''}`.trim()
        try {
            const identity = await IdentityEndpoints.createIdentity({
                clientId: Number(clientId),
                firstName: row.values.firstName,
                lastName: row.values.lastName,
                email: row.values.email || undefined,
            })

            const messages: string[] = []
            for (const patch of patchFieldsFor(row)) {
                try {
                    await IdentityEndpoints.editIdentity(identity.id, patch)
                } catch (error) {
                    messages.push(
                        `${patch.field}: ${extractErrorMessage(error, 'could not be saved')}`
                    )
                }
            }

            const pictureUrl = row.values.pictureUrl
            if (pictureUrl) {
                // `fetchRemoteImage` throws sentence fragments ("could not be
                // downloaded — …") so the row message reads as one line.
                let image: RemoteImage | undefined
                try {
                    image = await fetchRemoteImage(pictureUrl)
                } catch (error) {
                    messages.push(
                        `Photo ${extractErrorMessage(error, 'could not be downloaded')}`
                    )
                }
                if (image) {
                    try {
                        await IdentityEndpoints.editIdentity(identity.id, {
                            field: 'picture',
                            value: JSON.stringify({
                                base64Image: image.base64,
                                contentType: image.contentType,
                            }),
                        })
                    } catch (error) {
                        messages.push(
                            `Photo: ${extractErrorMessage(error, 'could not be saved')}`
                        )
                    }
                }
            }

            if (selectedEvent && roleId) {
                try {
                    await IdentityEndpoints.provisionForEvent(
                        identity.id,
                        selectedEvent.id,
                        {
                            roleId: Number(roleId),
                            accreditationLevelId:
                                accreditationId === NO_ACCREDITATION
                                    ? null
                                    : Number(accreditationId),
                            secondaryRoleName: secondaryRoleName.trim(),
                        },
                        true
                    )
                } catch (error) {
                    messages.push(
                        `Event provisioning: ${extractErrorMessage(error, 'failed')}`
                    )
                }
            }

            return {
                rowNumber: row.rowNumber,
                name,
                status: messages.length ? 'partial' : 'created',
                identityId: identity.id,
                messages,
            }
        } catch (error) {
            return {
                rowNumber: row.rowNumber,
                name,
                status: 'failed',
                messages: [extractErrorMessage(error, 'Failed to create identity')],
            }
        }
    }

    const runImport = async () => {
        const queue = rows.filter((r) => r.errors.length === 0)
        if (queue.length === 0) return

        // The page can sit open across the end of an event's last day, which drops
        // the selection out of the provisionable list.
        if (eventId !== NO_EVENT && !selectedEvent) {
            toast.error(
                'That event is no longer available to provision to — pick another one, or clear the event.'
            )
            return
        }

        cancelled.current = false
        setImporting(true)
        setFinished(false)
        setResults([])
        setCompleted(0)

        let cursor = 0
        const worker = async () => {
            while (!cancelled.current) {
                const index = cursor++
                if (index >= queue.length) return
                const result = await importRow(queue[index])
                setResults((prev) => [...prev, result])
                setCompleted((prev) => prev + 1)
            }
        }

        try {
            await Promise.all(
                Array.from({ length: Math.min(IMPORT_CONCURRENCY, queue.length) }, worker)
            )
        } finally {
            setImporting(false)
            setFinished(true)
        }
    }

    const downloadErrorReport = () => {
        if (!table) return
        const failedByRow = new Map(results.filter((r) => r.status !== 'created').map((r) => [r.rowNumber, r]))
        const header = [...table.headers, 'Import Status', 'Import Message']
        const body: string[][] = []

        rows.forEach((row) => {
            if (row.errors.length > 0) {
                body.push([...row.raw, 'Not imported', row.errors.join('; ')])
                return
            }
            const result = failedByRow.get(row.rowNumber)
            if (result) {
                body.push([
                    ...row.raw,
                    result.status === 'failed' ? 'Failed' : 'Imported with warnings',
                    result.messages.join('; '),
                ])
            }
        })

        if (body.length === 0) {
            toast.info('Nothing to report — every row imported cleanly.')
            return
        }
        downloadCsv(toCsv([header, ...body]), `identity-import-problems.csv`)
    }

    if (!isAdmin) {
        return (
            <div className="container mx-auto max-w-2xl px-4 py-6">
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-destructive" />
                            Not authorized
                        </CardTitle>
                        <CardDescription>
                            Bulk identity import is restricted to system administrators.
                        </CardDescription>
                    </CardHeader>
                </Card>
            </div>
        )
    }

    const activeClients = (clients || []).filter((c) => !c.dateArchived)
    const clientOptions = activeClients.map((c) => ({
        value: String(c.id),
        label: c.name,
        sublabel: c.region?.name,
    }))

    const schedulableRoles = (selectedEvent?.roles || [])
        .filter((r) => r.schedulable)
        .sort((a, b) => a.internalName.localeCompare(b.internalName))
    const otherRoles = (selectedEvent?.roles || [])
        .filter((r) => !r.schedulable)
        .sort((a, b) => a.internalName.localeCompare(b.internalName))

    const readyToMap = Boolean(clientId && table)
    const created = results.filter((r) => r.status === 'created').length
    const partial = results.filter((r) => r.status === 'partial').length
    const failed = results.filter((r) => r.status === 'failed').length
    const queueSize = summary.valid

    return (
        <div className="container mx-auto max-w-5xl px-4 py-6 space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
                        <Users className="h-6 w-6" />
                        Import Identities
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Create identities in bulk for one client from a CSV file. The
                        template has a column for every field — fill in the ones you have
                        and leave the rest blank.
                    </p>
                </div>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                            <Download className="h-4 w-4 mr-2" />
                            Template
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-72">
                        <DropdownMenuItem
                            onClick={() =>
                                downloadCsv(
                                    templateCsv(true),
                                    'identity-import-template.csv'
                                )
                            }
                        >
                            <div>
                                <p className="text-sm">All fields, with an example row</p>
                                <p className="text-xs text-muted-foreground">
                                    Every column we can import, plus one filled-in row
                                    showing the format
                                </p>
                            </div>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={() =>
                                downloadCsv(
                                    templateCsv(false),
                                    'identity-import-template-empty.csv'
                                )
                            }
                        >
                            <div>
                                <p className="text-sm">All fields, headers only</p>
                                <p className="text-xs text-muted-foreground">
                                    Ready to paste your own rows under
                                </p>
                            </div>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            {/* Step 1 — where the identities land */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">1. Destination</CardTitle>
                    <CardDescription>
                        Every identity in the file is created under this client.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                            <Label>Client</Label>
                            <Combobox
                                options={clientOptions}
                                value={clientId}
                                onChange={(next) => {
                                    setClientId(next)
                                    resetRun()
                                }}
                                placeholder="Select a client…"
                                searchPlaceholder="Search clients…"
                                emptyText="No clients found"
                                className="w-full"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="default-role">Badge role for everyone</Label>
                            <Input
                                id="default-role"
                                value={defaultBadgeRole}
                                onChange={(e) => setDefaultBadgeRole(e.target.value)}
                                placeholder="e.g. Security — optional"
                            />
                            <p className="text-xs text-muted-foreground">
                                Used for rows with no value in a mapped Badge Role column.
                            </p>
                        </div>
                    </div>

                    {clientId && (
                        <div className="rounded-md border p-4 space-y-4">
                            <div className="space-y-2">
                                <Label>Provision to an event (optional)</Label>
                                <Select
                                    value={eventId}
                                    onValueChange={(next) => {
                                        setEventId(next)
                                        setRoleId('')
                                        setAccreditationId(NO_ACCREDITATION)
                                    }}
                                    disabled={eventsLoading}
                                >
                                    <SelectTrigger>
                                        <SelectValue
                                            placeholder={
                                                eventsLoading ? 'Loading events…' : 'No event'
                                            }
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={NO_EVENT}>No event</SelectItem>
                                        {provisionableEvents.map((event) => (
                                            <SelectItem key={event.id} value={String(event.id)}>
                                                {event.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {!eventsLoading && provisionableEvents.length === 0 && (
                                    <p className="text-xs text-muted-foreground">
                                        {pastEventCount > 0
                                            ? `This client has no events that are still running — ${pastEventCount} past event${pastEventCount === 1 ? ' is' : 's are'} hidden.`
                                            : 'This client has no events yet.'}
                                    </p>
                                )}
                                {!eventsLoading &&
                                    provisionableEvents.length > 0 &&
                                    pastEventCount > 0 && (
                                        <p className="text-xs text-muted-foreground">
                                            {pastEventCount} past event
                                            {pastEventCount === 1 ? ' is' : 's are'} hidden —
                                            identities can only be provisioned to an event that
                                            has not ended.
                                        </p>
                                    )}
                            </div>

                            {selectedEvent && (
                                <div className="grid gap-4 sm:grid-cols-3">
                                    <div className="space-y-2">
                                        <Label>Event role</Label>
                                        <Select value={roleId} onValueChange={setRoleId}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select a role…" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {schedulableRoles.length > 0 && (
                                                    <SelectGroup>
                                                        <SelectLabel>Schedulable</SelectLabel>
                                                        {schedulableRoles.map((role) => (
                                                            <SelectItem
                                                                key={role.id}
                                                                value={String(role.id)}
                                                            >
                                                                {role.internalName}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectGroup>
                                                )}
                                                {otherRoles.length > 0 && (
                                                    <SelectGroup>
                                                        <SelectLabel>Other</SelectLabel>
                                                        {otherRoles.map((role) => (
                                                            <SelectItem
                                                                key={role.id}
                                                                value={String(role.id)}
                                                            >
                                                                {role.internalName}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectGroup>
                                                )}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Accreditation level</Label>
                                        <Select
                                            value={accreditationId}
                                            onValueChange={setAccreditationId}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value={NO_ACCREDITATION}>
                                                    Role default
                                                </SelectItem>
                                                {[...(selectedEvent.accreditationLevels || [])]
                                                    .sort((a, b) => a.name.localeCompare(b.name))
                                                    .map((level) => (
                                                        <SelectItem
                                                            key={level.id}
                                                            value={String(level.id)}
                                                        >
                                                            {level.name}
                                                        </SelectItem>
                                                    ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="secondary-role">Secondary role</Label>
                                        <Input
                                            id="secondary-role"
                                            value={secondaryRoleName}
                                            onChange={(e) => setSecondaryRoleName(e.target.value)}
                                            placeholder="Optional"
                                        />
                                    </div>
                                </div>
                            )}

                            {selectedEvent && !roleId && (
                                <p className="text-xs text-amber-600">
                                    Pick an event role, or set the event back to “No event” —
                                    identities are created either way, but nothing is provisioned
                                    without a role.
                                </p>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Step 2 — the file */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">2. CSV file</CardTitle>
                    <CardDescription>
                        One identity per row, with a header row naming the columns.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.txt,text/csv"
                        className="hidden"
                        onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) loadFile(file)
                        }}
                    />
                    {table ? (
                        <div className="flex items-center justify-between gap-4 rounded-md border p-4">
                            <div className="flex items-center gap-3 min-w-0">
                                <FileSpreadsheet className="h-5 w-5 text-muted-foreground shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-sm font-medium truncate">{fileName}</p>
                                    <p className="text-xs text-muted-foreground">
                                        {table.rows.length} row
                                        {table.rows.length === 1 ? '' : 's'} ·{' '}
                                        {table.headers.length} column
                                        {table.headers.length === 1 ? '' : 's'}
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={clearFile}
                                disabled={importing}
                            >
                                <X className="h-4 w-4 mr-2" />
                                Remove
                            </Button>
                        </div>
                    ) : (
                        <div
                            onDragOver={(e) => {
                                e.preventDefault()
                                setDragging(true)
                            }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={(e) => {
                                e.preventDefault()
                                setDragging(false)
                                const file = e.dataTransfer.files?.[0]
                                if (file) loadFile(file)
                            }}
                            className={`flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-8 text-center transition-colors ${
                                dragging ? 'border-primary bg-primary/5' : 'border-muted'
                            }`}
                        >
                            <Upload className="h-6 w-6 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">
                                Drop a CSV here, or
                            </p>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Choose a file
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Step 3 — column mapping */}
            {readyToMap && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">3. Map columns</CardTitle>
                        <CardDescription>
                            We guessed from the header names — change anything that looks wrong.
                            Values are trimmed on the way in; unmapped columns are ignored.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-[30%]">CSV column</TableHead>
                                        <TableHead className="w-[30%]">First value</TableHead>
                                        <TableHead>Identity field</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {table.headers.map((header, index) => {
                                        const current = mapping[index] || IGNORE_COLUMN
                                        const sample =
                                            table.rows.find((r) => (r[index] || '').trim())?.[
                                                index
                                            ] || ''
                                        return (
                                            <TableRow key={`${header}-${index}`}>
                                                <TableCell className="font-medium">
                                                    {header}
                                                </TableCell>
                                                <TableCell className="text-muted-foreground truncate max-w-[220px]">
                                                    {sample.trim() || '—'}
                                                </TableCell>
                                                <TableCell>
                                                    <Select
                                                        value={current}
                                                        onValueChange={(next) => {
                                                            setMapping((prev) => {
                                                                const updated = { ...prev }
                                                                if (next !== IGNORE_COLUMN) {
                                                                    // A field can only come from one column.
                                                                    Object.keys(updated).forEach(
                                                                        (k) => {
                                                                            if (
                                                                                updated[Number(k)] ===
                                                                                next
                                                                            ) {
                                                                                updated[Number(k)] =
                                                                                    IGNORE_COLUMN
                                                                            }
                                                                        }
                                                                    )
                                                                }
                                                                updated[index] =
                                                                    next as ImportFieldKey
                                                                return updated
                                                            })
                                                            resetRun()
                                                        }}
                                                        disabled={importing}
                                                    >
                                                        <SelectTrigger className="max-w-[280px]">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value={IGNORE_COLUMN}>
                                                                Don’t import
                                                            </SelectItem>
                                                            {GROUP_ORDER.map((group) => {
                                                                const fields = IMPORT_FIELDS.filter(
                                                                    (f) => f.group === group
                                                                )
                                                                if (fields.length === 0) return null
                                                                return (
                                                                    <SelectGroup key={group}>
                                                                        <SelectLabel>
                                                                            {
                                                                                FIELD_GROUP_LABELS[
                                                                                    group
                                                                                ]
                                                                            }
                                                                        </SelectLabel>
                                                                        {fields.map((field) => (
                                                                            <SelectItem
                                                                                key={field.key}
                                                                                value={field.key}
                                                                            >
                                                                                {field.label}
                                                                                {mappedFields.has(
                                                                                    field.key
                                                                                ) &&
                                                                                    current !==
                                                                                        field.key &&
                                                                                    ' (in use)'}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectGroup>
                                                                )
                                                            })}
                                                        </SelectContent>
                                                    </Select>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Step 4 — review + run */}
            {readyToMap && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">4. Review and import</CardTitle>
                        <CardDescription>
                            {summary.valid} of {summary.total} rows are ready
                            {summary.invalid > 0
                                ? ` — ${summary.invalid} will be skipped.`
                                : '.'}
                            {scanningExisting && ' Checking for existing emails…'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex flex-wrap gap-2">
                            <Badge variant="secondary">
                                {summary.valid} ready
                            </Badge>
                            {summary.invalid > 0 && (
                                <Badge variant="destructive">{summary.invalid} with errors</Badge>
                            )}
                            {photoCount > 0 && (
                                <Badge variant="outline" className="gap-1">
                                    <ImageIcon className="h-3 w-3" />
                                    {photoCount} with a photo
                                </Badge>
                            )}
                            {selectedClient && (
                                <Badge variant="outline">Client: {selectedClient.name}</Badge>
                            )}
                            {selectedEvent && roleId && (
                                <Badge variant="outline">
                                    Provisioning to {selectedEvent.name}
                                </Badge>
                            )}
                        </div>

                        {photoCount > 0 && (
                            <p className="text-xs text-muted-foreground">
                                Each photo is downloaded by this browser, so the import runs
                                slower and links on a host that blocks cross-origin requests
                                will fail. The identity is still created — the photo just
                                shows up in the problem rows.
                            </p>
                        )}

                        {summary.invalid > 0 && (
                            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
                                <p className="text-sm font-medium flex items-center gap-2 mb-2">
                                    <AlertTriangle className="h-4 w-4 text-destructive" />
                                    Rows that will be skipped
                                </p>
                                <ul className="space-y-1 text-sm text-muted-foreground max-h-40 overflow-y-auto">
                                    {rows
                                        .filter((r) => r.errors.length > 0)
                                        .slice(0, 25)
                                        .map((r) => (
                                            <li key={r.rowNumber}>
                                                Row {r.rowNumber}: {r.errors.join(', ')}
                                            </li>
                                        ))}
                                    {summary.invalid > 25 && (
                                        <li>…and {summary.invalid - 25} more.</li>
                                    )}
                                </ul>
                            </div>
                        )}

                        {results.length === 0 && !importing && summary.valid > 0 && (
                            <div className="overflow-x-auto rounded-md border">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-12">#</TableHead>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Email</TableHead>
                                            <TableHead>Company</TableHead>
                                            <TableHead>Badge role</TableHead>
                                            {photoCount > 0 && <TableHead>Photo</TableHead>}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {rows
                                            .filter((r) => r.errors.length === 0)
                                            .slice(0, PREVIEW_ROWS)
                                            .map((r) => (
                                                <TableRow key={r.rowNumber}>
                                                    <TableCell className="text-muted-foreground">
                                                        {r.rowNumber}
                                                    </TableCell>
                                                    <TableCell>
                                                        {r.values.firstName} {r.values.lastName}
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground">
                                                        {r.values.email || '—'}
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground">
                                                        {r.values.companyName || '—'}
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground">
                                                        {r.values.clientBadgeRoleName || '—'}
                                                    </TableCell>
                                                    {photoCount > 0 && (
                                                        <TableCell className="text-muted-foreground">
                                                            {r.values.pictureUrl ? (
                                                                <span
                                                                    title={r.values.pictureUrl}
                                                                >
                                                                    <ImageIcon className="h-4 w-4" />
                                                                </span>
                                                            ) : (
                                                                '—'
                                                            )}
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                                {summary.valid > PREVIEW_ROWS && (
                                    <p className="px-3 py-2 text-xs text-muted-foreground border-t">
                                        Showing the first {PREVIEW_ROWS} of {summary.valid} rows.
                                    </p>
                                )}
                            </div>
                        )}

                        {(importing || finished) && (
                            <div className="space-y-2">
                                <Progress
                                    value={queueSize ? (completed / queueSize) * 100 : 0}
                                />
                                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                                    <span>
                                        {completed} of {queueSize} processed
                                    </span>
                                    <span className="flex items-center gap-1 text-green-600">
                                        <CheckCircle2 className="h-4 w-4" />
                                        {created} created
                                    </span>
                                    {partial > 0 && (
                                        <span className="flex items-center gap-1 text-amber-600">
                                            <AlertTriangle className="h-4 w-4" />
                                            {partial} with warnings
                                        </span>
                                    )}
                                    {failed > 0 && (
                                        <span className="flex items-center gap-1 text-destructive">
                                            <XCircle className="h-4 w-4" />
                                            {failed} failed
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        {results.some((r) => r.status !== 'created') && (
                            <div className="rounded-md border max-h-64 overflow-y-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-12">#</TableHead>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Problem</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {results
                                            .filter((r) => r.status !== 'created')
                                            .sort((a, b) => a.rowNumber - b.rowNumber)
                                            .map((r) => (
                                                <TableRow key={r.rowNumber}>
                                                    <TableCell className="text-muted-foreground">
                                                        {r.rowNumber}
                                                    </TableCell>
                                                    <TableCell>{r.name}</TableCell>
                                                    <TableCell className="text-sm">
                                                        {r.messages.join('; ')}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}

                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                onClick={runImport}
                                disabled={
                                    importing ||
                                    summary.valid === 0 ||
                                    !clientId ||
                                    (finished && results.length > 0)
                                }
                            >
                                {importing ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Importing…
                                    </>
                                ) : (
                                    <>
                                        <Upload className="h-4 w-4 mr-2" />
                                        Import {summary.valid} identit
                                        {summary.valid === 1 ? 'y' : 'ies'}
                                    </>
                                )}
                            </Button>

                            {importing && (
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        cancelled.current = true
                                        toast.info(
                                            'Stopping after the rows already in flight finish.'
                                        )
                                    }}
                                >
                                    Stop
                                </Button>
                            )}

                            {finished && (
                                <>
                                    <Button variant="outline" onClick={downloadErrorReport}>
                                        <Download className="h-4 w-4 mr-2" />
                                        Download problem rows
                                    </Button>
                                    <Button variant="ghost" onClick={clearFile}>
                                        <RotateCcw className="h-4 w-4 mr-2" />
                                        Start another import
                                    </Button>
                                </>
                            )}
                        </div>

                        {finished && (
                            <p className="text-sm text-muted-foreground">
                                Finished — {created} created
                                {partial > 0 ? `, ${partial} created with warnings` : ''}
                                {failed > 0 ? `, ${failed} failed` : ''}
                                {summary.invalid > 0 ? `, ${summary.invalid} skipped` : ''}.
                            </p>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    )
}
